import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import api from '../services/api';
import { tourManager as tourManagerApi } from '../services/api';
import aiApi from '../services/ai';
import InlineStatusSelect from './InlineStatusSelect';
import './TourManager.css';

const STATUTS_TACHE = [
  { value: 'en_attente', label: 'En attente', color: '#f59e0b' },
  { value: 'en_cours',   label: 'En cours',   color: '#3b82f6' },
  { value: 'termine',    label: 'Terminée',   color: '#10b981' },
];
const STATUT_LABEL = Object.fromEntries(STATUTS_TACHE.map(s => [s.value, s.label]));

// ─── En-tête de colonne triable ───────────────────────────────────────────────
function SortableTh({ label, field, sort, dir, onSort, style }) {
  const active = sort === field;
  return (
    <th onClick={() => onSort(field)} className="tm-th-sortable" style={style}>
      {label}
      <span className={`tm-sort-arrow${active ? ' active' : ''}`}>{active ? (dir === 'desc' ? '▼' : '▲') : '↕'}</span>
    </th>
  );
}

// ─── Lightbox photo ───────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="tm-lightbox" onClick={onClose}>
      <img src={`data:image/jpeg;base64,${src}`} alt="Photo agrandie" onClick={e => e.stopPropagation()} />
    </div>
  );
}

// ─── Modal détail tâche ───────────────────────────────────────────────────────
function TacheModal({ id, user, responsables, onClose, onUpdated }) {
  const [tache, setTache]           = useState(null);
  const [photos, setPhotos]         = useState([]);
  const [comments, setComments]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [mapPhotoIdx, setMapPhotoIdx] = useState(0);

  const [assignId, setAssignId]       = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [terminerSaving, setTerminerSaving] = useState(false);
  const [statutSaving, setStatutSaving] = useState(false);
  const [planifDate, setPlanifDate]   = useState('');
  const [planifSaving, setPlanifSaving] = useState(false);
  const [dateLimite, setDateLimite]     = useState('');
  const [dateLimiteSaving, setDateLimiteSaving] = useState(false);
  const [priorite, setPriorite]         = useState('');
  const [prioriteSaving, setPrioriteSaving] = useState(false);

  const [editingAction, setEditingAction] = useState(false);
  const [actionDraft, setActionDraft]     = useState('');
  const [actionSaving, setActionSaving]   = useState(false);

  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [reformulation, setReformulation] = useState('');
  const [reformulating, setReformulating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tour-manager/${id}`);
      setTache(r.data.tache);
      setPhotos(r.data.photos || []);
      setComments(r.data.comments || []);
      setMapPhotoIdx(0);
      setAssignId(r.data.tache.responsable_id ? String(r.data.tache.responsable_id) : '');
      setPlanifDate(r.data.tache.date_planification || '');
      setDateLimite(r.data.tache.date_limite || '');
      setPriorite(r.data.tache.priorite || '');
      setActionDraft(r.data.tache.action || '');
      setEditingAction(false);
    } catch (err) {
      console.error('[TourManager] Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !lightboxSrc) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, lightboxSrc]);

  const handleReformuler = async () => {
    if (!tache?.action) return;
    setReformulating(true);
    try {
      const { data } = await aiApi.synthesize(tache.action);
      setReformulation(data.synthese || tache.action);
    } catch {
      setReformulation('');
    } finally {
      setReformulating(false);
    }
  };

  const handleAssign = async () => {
    if (!assignId) return;
    setAssignSaving(true);
    try {
      const resp = responsables.find(r => String(r.id) === assignId);
      await api.put(`/tour-manager/${id}/assigner`, {
        responsable_id:  parseInt(assignId),
        responsable_nom: resp ? `${resp.prenom ? resp.prenom + ' ' : ''}${resp.nom}` : null,
      });
      onUpdated();
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setAssignSaving(false);
    }
  };

  const handleTerminer = async () => {
    if (!window.confirm('Marquer cette tâche comme terminée ?')) return;
    setTerminerSaving(true);
    try {
      await api.put(`/tour-manager/${id}/terminer`);
      onUpdated();
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
      setTerminerSaving(false);
    }
  };

  const handlePlanifier = async () => {
    setPlanifSaving(true);
    try {
      await api.put(`/tour-manager/${id}/planifier`, { date_planification: planifDate || null });
      onUpdated();
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setPlanifSaving(false);
    }
  };

  const handleDateLimite = async () => {
    setDateLimiteSaving(true);
    try {
      await api.put(`/tour-manager/${id}/date-limite`, { date_limite: dateLimite || null });
      onUpdated();
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setDateLimiteSaving(false);
    }
  };

  const handleEnCours = async () => {
    setStatutSaving(true);
    try {
      await api.put(`/tour-manager/${id}/statut`, { statut: 'en_cours' });
      onUpdated();
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setStatutSaving(false);
    }
  };

  const handleSaveAction = async () => {
    if (!actionDraft.trim()) return;
    setActionSaving(true);
    try {
      await api.put(`/tour-manager/${id}`, { action: actionDraft.trim() });
      onUpdated();
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setActionSaving(false);
    }
  };

  const handlePriorite = async (val) => {
    setPriorite(val);
    setPrioriteSaving(true);
    try {
      await api.put(`/tour-manager/${id}/priorite`, { priorite: val || null });
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setPrioriteSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSavingComment(true);
    try {
      const r = await api.post(`/tour-manager/${id}/commentaires`, { contenu: commentText.trim() });
      setComments(prev => [...prev, r.data.comment]);
      setCommentText('');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await api.delete(`/tour-manager/commentaires/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {}
  };

  if (loading) return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="tm-modal-loading">Chargement…</div>
      </div>
    </div>
  );
  if (!tache) return null;

  const photosWithCoords = photos.filter(p => p.latitude && p.longitude);

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content large" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="modal-header">
            <div className="modal-header-title">
              <span className={`tm-statut-badge tm-statut-${tache.statut}`}>
                {STATUT_LABEL[tache.statut] || tache.statut}
              </span>
            </div>
            <button className="modal-close" onClick={onClose} aria-label="Fermer">✕</button>
          </div>

          {/* Action + reformulation IA */}
          <div className="tm-modal-action-block">
            {editingAction ? (
              <div className="tm-action-edit">
                <textarea
                  className="tm-new-textarea"
                  rows={4}
                  value={actionDraft}
                  onChange={e => setActionDraft(e.target.value)}
                  autoFocus
                />
                <div className="tm-new-actions" style={{ paddingTop: 8 }}>
                  <button className="tm-btn-cancel" onClick={() => { setEditingAction(false); setActionDraft(tache.action); }}>
                    Annuler
                  </button>
                  <button className="tm-btn-save" onClick={handleSaveAction} disabled={actionSaving || !actionDraft.trim()}>
                    {actionSaving ? '⏳ Enregistrement…' : '✓ Enregistrer'}
                  </button>
                </div>
              </div>
            ) : reformulation ? (
              <div className="tm-reformulation">
                <div className="tm-reformulation-label">✨ Version corrigée</div>
                <p>{reformulation}</p>
                <button className="tm-btn-reformat-reset" onClick={() => setReformulation('')}>
                  Voir original
                </button>
              </div>
            ) : (
              <div className="tm-action-original">
                <p>{tache.action}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="tm-btn-reformat"
                    onClick={handleReformuler}
                    disabled={reformulating}
                    title="Corriger l'orthographe et la syntaxe avec l'IA"
                  >
                    {reformulating ? '⏳ Reformulation…' : '✨ Reformuler avec l\'IA'}
                  </button>
                  <button className="tm-btn-reformat" onClick={() => setEditingAction(true)}>
                    ✏️ Modifier
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Meta */}
          <div className="tm-modal-meta">
            <span>📅 {new Date(tache.created_at).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
            {tache.concession_nom && <span>🏢 {tache.concession_nom}</span>}
            <span>📷 {photos.length} photo{photos.length > 1 ? 's' : ''}</span>
          </div>

          {/* Body 2 colonnes */}
          <div className="modal-body tm-modal-body-grid">

            {/* ── Gauche ── */}
            <div className="tm-modal-left">

              {/* Affectation */}
              <div className="tm-section">
                <div className="tm-section-title">Affectation</div>
                {tache.responsable_nom ? (
                  <div className="tm-assigned-to">
                    <span className="tm-assigned-icon">👤</span>
                    <span>{tache.responsable_nom}</span>
                  </div>
                ) : (
                  <div className="tm-unassigned">Non assignée</div>
                )}
                {tache.statut !== 'termine' && (
                  <div className="tm-assign-form">
                    <select
                      value={assignId}
                      onChange={e => setAssignId(e.target.value)}
                      className="tm-select"
                    >
                      <option value="">— Sélectionner un responsable</option>
                      {responsables.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.prenom ? `${r.prenom} ` : ''}{r.nom}
                        </option>
                      ))}
                    </select>
                    <button
                      className="tm-btn-assign"
                      onClick={handleAssign}
                      disabled={!assignId || assignSaving}
                    >
                      {assignSaving ? '…' : '✓ Affecter'}
                    </button>
                  </div>
                )}
              </div>

              {/* Dates */}
              {tache.date_traitement && (
                <div className="tm-section">
                  <div className="tm-section-title">Traitement</div>
                  <div className="tm-info-row">
                    <label>Terminée le</label>
                    <span>{new Date(tache.date_traitement).toLocaleString('fr-FR')}</span>
                  </div>
                </div>
              )}

              {/* Planification */}
              {tache.statut !== 'termine' && (
                <div className="tm-section">
                  <div className="tm-section-title">Planification</div>
                  <div className="tm-assign-form">
                    <input
                      type="date"
                      value={planifDate}
                      onChange={e => setPlanifDate(e.target.value)}
                      className="tm-select"
                      style={{ flex: 1 }}
                    />
                    <button
                      className="tm-btn-assign"
                      onClick={handlePlanifier}
                      disabled={planifSaving}
                    >
                      {planifSaving ? '…' : '✓ Planifier'}
                    </button>
                  </div>
                  {tache.date_planification && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#0369a1' }}>
                      📅 Planifiée au {new Date(tache.date_planification).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                </div>
              )}

              {/* Date limite */}
              {tache.statut !== 'termine' && (
                <div className="tm-section">
                  <div className="tm-section-title">Date limite</div>
                  <div className="tm-assign-form">
                    <input
                      type="date"
                      value={dateLimite}
                      onChange={e => setDateLimite(e.target.value)}
                      className="tm-select"
                      style={{ flex: 1 }}
                    />
                    <button
                      className="tm-btn-assign"
                      onClick={handleDateLimite}
                      disabled={dateLimiteSaving}
                    >
                      {dateLimiteSaving ? '…' : '✓ Enregistrer'}
                    </button>
                  </div>
                  {tache.date_limite && (() => {
                    const todayStr = new Date().toISOString().split('T')[0];
                    const overdue = tache.date_limite < todayStr;
                    return (
                      <div style={{ marginTop: '6px', fontSize: '12px', color: overdue ? '#dc2626' : '#0369a1', fontWeight: overdue ? 700 : 400 }}>
                        ⏰ Échéance au {new Date(tache.date_limite).toLocaleDateString('fr-FR')}{overdue ? ' — dépassée' : ''}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Priorité */}
              <div className="tm-section">
                <div className="tm-section-title">
                  Priorité
                  {prioriteSaving && <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '6px' }}>…</span>}
                </div>
                <div className="tm-priorite-btns">
                  <button
                    type="button"
                    className={`tm-priorite-btn${priorite === '' ? ' tm-priorite-none-active' : ''}`}
                    onClick={() => handlePriorite('')}
                    disabled={prioriteSaving}
                  >
                    Aucune
                  </button>
                  {PRIORITES.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      className={`tm-priorite-btn tm-priorite-btn-color${priorite === p.value ? ' active' : ''}`}
                      style={{ background: p.color, borderColor: priorite === p.value ? '#1e293b' : p.color, boxShadow: priorite === p.value ? `0 0 0 3px ${p.color}55` : 'none' }}
                      title={p.value.charAt(0).toUpperCase() + p.value.slice(1)}
                      onClick={() => handlePriorite(p.value)}
                      disabled={prioriteSaving}
                    />
                  ))}
                </div>
              </div>

              {/* Commentaires */}
              <div className="tm-section tm-section-comments">
                <div className="tm-section-title">
                  Commentaires
                  <span className="tm-count-badge">{comments.length}</span>
                </div>
                <div className="tm-comments-list">
                  {comments.length === 0 && (
                    <p className="tm-empty-msg">Aucun commentaire</p>
                  )}
                  {comments.map(c => (
                    <div key={c.id} className="tm-comment">
                      <div className="tm-comment-header">
                        <span className="tm-comment-user">{c.user_prenom} {c.user_nom}</span>
                        <span className="tm-comment-date">
                          {new Date(c.created_at).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                        </span>
                        {c.user_id === user?.id && (
                          <button className="tm-comment-del" onClick={() => handleDeleteComment(c.id)}>✕</button>
                        )}
                      </div>
                      <p className="tm-comment-text">{c.contenu}</p>
                    </div>
                  ))}
                </div>
                <div className="tm-comment-form">
                  <textarea
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    placeholder="Ajouter un commentaire…"
                    rows={2}
                    className="tm-comment-input"
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                  />
                  <button className="tm-btn-comment" onClick={handleAddComment} disabled={!commentText.trim() || savingComment}>
                    {savingComment ? '…' : 'Envoyer'}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Droite ── */}
            <div className="tm-modal-right">

              {/* Photos */}
              <div className="tm-section">
                <div className="tm-section-title">
                  Photos
                  <span className="tm-count-badge">{photos.length}</span>
                </div>
                {photos.length === 0 ? (
                  <p className="tm-empty-msg">Aucune photo</p>
                ) : (
                  <div className="tm-photos-grid">
                    {photos.map((p, i) => (
                      <div
                        key={p.id}
                        className="tm-photo-thumb"
                        onClick={() => setLightboxSrc(p.photo_data)}
                        title={p.latitude ? `📍 ${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}` : ''}
                      >
                        <img src={`data:image/jpeg;base64,${p.photo_data}`} alt={`Photo ${i + 1}`} />
                        {p.latitude && <span className="tm-photo-gps">📍</span>}
                        <span className="tm-photo-num">{i + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Carte Google Maps embarquée */}
              {photosWithCoords.length > 0 && (
                <div className="tm-section">
                  <div className="tm-section-title">
                    Géolocalisation — Vue aérienne
                    {photosWithCoords.length > 1 && (
                      <span className="tm-map-nav">
                        <button
                          className="tm-map-nav-btn"
                          onClick={() => setMapPhotoIdx(i => Math.max(0, i - 1))}
                          disabled={mapPhotoIdx === 0}
                        >‹</button>
                        <span className="tm-map-nav-label">
                          Photo {mapPhotoIdx + 1}/{photosWithCoords.length}
                        </span>
                        <button
                          className="tm-map-nav-btn"
                          onClick={() => setMapPhotoIdx(i => Math.min(photosWithCoords.length - 1, i + 1))}
                          disabled={mapPhotoIdx === photosWithCoords.length - 1}
                        >›</button>
                      </span>
                    )}
                  </div>
                  <iframe
                    key={`${photosWithCoords[mapPhotoIdx].latitude},${photosWithCoords[mapPhotoIdx].longitude}`}
                    className="tm-map-iframe"
                    src={`https://maps.google.com/maps?q=${photosWithCoords[mapPhotoIdx].latitude},${photosWithCoords[mapPhotoIdx].longitude}&t=k&z=18&output=embed`}
                    allowFullScreen
                    loading="lazy"
                    title="Géolocalisation"
                  />
                </div>
              )}

            </div>
          </div>

          {/* Footer */}
          {tache.statut !== 'termine' && (
            <div className="modal-footer">
              {tache.statut === 'en_attente' && (
                <button className="tm-btn-encours" onClick={handleEnCours} disabled={statutSaving}>
                  {statutSaving ? '⏳…' : '🔧 Démarrer (En cours)'}
                </button>
              )}
              <button className="tm-btn-terminer" onClick={handleTerminer} disabled={terminerSaving}>
                {terminerSaving ? '⏳ En cours…' : '✅ Marquer comme terminée'}
              </button>
            </div>
          )}
        </div>
      </div>

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </>
  );
}

// ─── Modale nouvelle tâche (depuis CRM) ──────────────────────────────────────
const PRIORITES = [
  { value: 'rouge',  color: '#ef4444' },
  { value: 'orange', color: '#f97316' },
  { value: 'jaune',  color: '#eab308' },
];

function NouvellesTacheModal({ user, concessions, responsables, onClose, onCreated }) {
  const [action,        setAction]        = useState('');
  const [agent,         setAgent]         = useState('');
  const [responsableId, setResponsableId] = useState('');
  const [priorite,      setPriorite]      = useState('');
  const [dateLimite,    setDateLimite]    = useState('');
  const [photos,        setPhotos]        = useState([]);   // [{ data: base64, name }]
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const fileRef = useRef(null);

  // Concession : un utilisateur rattaché à une concession (fixe, y compris via
  // le sélecteur global super-admin/téléopérateur) n'a rien à choisir. Sinon
  // (super-admin/téléopérateur en vue "toutes concessions"), un choix explicite
  // est requis — on part du dernier choisi pour ne pas le redemander à chaque fois.
  const needsConcessionPicker = (user?.role === 'super-admin' || user?.role === 'teleoperateur') && !user?.concession_id;
  const [concessionId, setConcessionId] = useState(() => {
    if (!needsConcessionPicker) return user?.concession_id ? String(user.concession_id) : '';
    return localStorage.getItem('last_concession_id_tour_manager') || '';
  });

  const handleConcessionChange = (id) => {
    setConcessionId(id);
    setResponsableId('');
    if (needsConcessionPicker) localStorage.setItem('last_concession_id_tour_manager', id);
  };

  const responsablesFiltres = concessionId
    ? responsables.filter(r => String(r.concession_id) === String(concessionId))
    : responsables;

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result.split(',')[1]; // strip data:image/...;base64,
        setPhotos(prev => [...prev, { data: base64, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!action.trim()) { setError('Le champ "Action" est obligatoire.'); return; }
    if (needsConcessionPicker && !concessionId) { setError('La concession est obligatoire.'); return; }
    setSaving(true);
    setError('');
    try {
      const resp = responsables.find(r => String(r.id) === responsableId);
      await tourManagerApi.createTache({
        action:          action.trim(),
        agent:           agent.trim() || null,
        responsable_id:  resp ? resp.id : null,
        responsable_nom: resp ? `${resp.prenom ? resp.prenom + ' ' : ''}${resp.nom}` : null,
        concession_id:   concessionId ? parseInt(concessionId) : null,
        priorite:        priorite || null,
        date_limite:     dateLimite || null,
        photos:          photos.map(p => ({ data: p.data })),
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>

        <div className="modal-header">
          <div className="modal-header-title">➕ Nouvelle tâche</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="tm-new-form">

          {/* Action */}
          <div className="tm-new-field">
            <label className="tm-new-label">Action <span className="tm-required">*</span></label>
            <textarea
              className="tm-new-textarea"
              rows={4}
              placeholder="Décrivez la tâche à réaliser…"
              value={action}
              onChange={e => setAction(e.target.value)}
              autoFocus
            />
          </div>

          {/* Priorité */}
          <div className="tm-new-field">
            <label className="tm-new-label">Priorité</label>
            <div className="tm-priorite-btns">
              <button
                type="button"
                className={`tm-priorite-btn${priorite === '' ? ' tm-priorite-none-active' : ''}`}
                onClick={() => setPriorite('')}
              >
                Aucune
              </button>
              {PRIORITES.map(p => (
                <button
                  key={p.value}
                  type="button"
                  className={`tm-priorite-btn tm-priorite-btn-color${priorite === p.value ? ' active' : ''}`}
                  style={{ background: p.color, borderColor: priorite === p.value ? '#1e293b' : p.color, boxShadow: priorite === p.value ? `0 0 0 3px ${p.color}55` : 'none' }}
                  title={p.value.charAt(0).toUpperCase() + p.value.slice(1)}
                  onClick={() => setPriorite(p.value)}
                />
              ))}
            </div>
          </div>

          {/* Concession — uniquement si ambiguë (super-admin/téléopérateur "toutes concessions") */}
          {needsConcessionPicker && (
            <div className="tm-new-field">
              <label className="tm-new-label">Concession <span className="tm-required">*</span></label>
              <select
                className="tm-new-input"
                value={concessionId}
                onChange={e => handleConcessionChange(e.target.value)}
              >
                <option value="">— Sélectionner une concession</option>
                {concessions.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            </div>
          )}

          {/* Agent */}
          <div className="tm-new-field">
            <label className="tm-new-label">Agent signalant</label>
            <input
              className="tm-new-input"
              type="text"
              placeholder="Nom de l'agent (optionnel)"
              value={agent}
              onChange={e => setAgent(e.target.value)}
            />
          </div>

          {/* Responsable */}
          <div className="tm-new-field">
            <label className="tm-new-label">Responsable assigné</label>
            <select
              className="tm-new-input"
              value={responsableId}
              onChange={e => setResponsableId(e.target.value)}
            >
              <option value="">— Non assigné</option>
              {responsablesFiltres.map(r => (
                <option key={r.id} value={r.id}>
                  {r.prenom ? `${r.prenom} ` : ''}{r.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Date limite */}
          <div className="tm-new-field">
            <label className="tm-new-label">Date limite</label>
            <input
              className="tm-new-input"
              type="date"
              value={dateLimite}
              onChange={e => setDateLimite(e.target.value)}
            />
          </div>

          {/* Photos */}
          <div className="tm-new-field">
            <label className="tm-new-label">Photos</label>
            <button
              type="button"
              className="tm-new-btn-photo"
              onClick={() => fileRef.current?.click()}
            >
              📷 Ajouter des photos
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handlePhotos}
            />
            {photos.length > 0 && (
              <div className="tm-new-photos-preview">
                {photos.map((p, i) => (
                  <div key={i} className="tm-new-photo-thumb">
                    <img src={`data:image/jpeg;base64,${p.data}`} alt={p.name} />
                    <button className="tm-new-photo-del" onClick={() => removePhoto(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="tm-new-error">{error}</div>}

          <div className="tm-new-actions">
            <button className="tm-btn-cancel" onClick={onClose}>Annuler</button>
            <button
              className="tm-btn-save"
              onClick={handleSubmit}
              disabled={saving || !action.trim() || (needsConcessionPicker && !concessionId)}
            >
              {saving ? '⏳ Enregistrement…' : '✅ Créer la tâche'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Carte Kanban — contenu partagé (carte réelle + aperçu DragOverlay) ────────
function KanbanCardContent({ t, todayStr }) {
  const overdue = t.statut !== 'termine' && t.date_limite && t.date_limite < todayStr;
  return (
    <>
      <div className="tm-kanban-card-top">
        {(() => {
          const p = PRIORITES.find(p => p.value === t.priorite);
          return p ? <span className="tm-priorite-dot" style={{ background: p.color }} title={p.value} /> : null;
        })()}
        <span className="tm-kanban-card-date">{new Date(t.created_at).toLocaleDateString('fr-FR')}</span>
      </div>
      <div className="tm-kanban-card-title">
        {t.action.length > 110 ? t.action.slice(0, 110) + '…' : t.action}
      </div>
      <div className="tm-kanban-card-footer">
        {t.responsable_nom
          ? <span className="tm-resp-name">👤 {t.responsable_nom}</span>
          : <span className="tm-unassigned-tag">Non assignée</span>}
        {t.date_limite && (
          <span className={`tm-echeance-badge${overdue ? ' tm-echeance-overdue' : ''}`}>
            ⏰ {new Date(t.date_limite).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
          </span>
        )}
        {t.nb_photos > 0 && <span className="tm-photos-badge">📷 {t.nb_photos}</span>}
      </div>
    </>
  );
}

// ─── Carte Kanban — glissable (@dnd-kit, événements pointeur) ─────────────────
function TacheKanbanCard({ t, todayStr, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: t.id });
  const p = PRIORITES.find(p => p.value === t.priorite);
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, borderLeftColor: p ? p.color : 'var(--border-default)' }}
      className={`tm-kanban-card${isDragging ? ' is-dragging' : ''}`}
      onClick={() => onOpen(t.id)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onOpen(t.id)}
      {...attributes}
      {...listeners}
    >
      <KanbanCardContent t={t} todayStr={todayStr} />
    </div>
  );
}

// ─── Colonne Kanban — zone de dépôt (@dnd-kit) ────────────────────────────────
function TacheKanbanColumn({ s, list, todayStr, isDragActive, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: s.value });
  return (
    <div ref={setNodeRef} className={`tm-kanban-col${isOver ? ' drag-over' : ''}`}>
      <div className="tm-kanban-col-head">
        <span className="tm-priorite-dot" style={{ background: s.color }} />
        <span style={{ color: s.color, fontWeight: 700 }}>{s.label}</span>
        <span className="tm-count-badge">{list.length}</span>
      </div>
      <div className="tm-kanban-cards">
        {list.map(t => (
          <TacheKanbanCard key={t.id} t={t} todayStr={todayStr} onOpen={onOpen} />
        ))}
        {list.length === 0 && (
          <div className="tm-kanban-empty">{isDragActive ? 'Déposer ici' : 'Aucune tâche'}</div>
        )}
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
function TourManager({ user, concessions, setHeaderAction }) {
  const [mode, setMode]           = useState('actives');
  const [viewMode, setViewMode]   = useState('table'); // 'table' | 'kanban'
  const [taches, setTaches]       = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters]     = useState({ concession_id: '', responsable_id: '', search: '' });
  const [responsables, setResponsables] = useState([]);
  const [showTout, setShowTout]   = useState(false);
  const [showNewTache, setShowNewTache] = useState(false);
  const [sortKey, setSortKey]     = useState(null);
  const [sortDir, setSortDir]     = useState('asc');

  // Drag & drop (kanban) — @dnd-kit, activation seulement au-delà d'un seuil de
  // déplacement pour ne pas interférer avec le simple clic (ouverture de fiche).
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [activeDragId, setActiveDragId] = useState(null);

  // Boutons d'action dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    if (user?.role === 'super-admin') {
      setHeaderAction({
        primary: { label: '+ Ajouter une tâche', onClick: () => setShowNewTache(true) },
      });
    } else {
      setHeaderAction({});
    }
  }, [setHeaderAction, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get('/tour-manager/stats');
      setStats(r.data);
    } catch {}
  }, []);

  const loadTaches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // En vue Kanban, on veut toutes les tâches (tous statuts) d'un coup d'œil —
      // le filtre par statut (mode) ne s'applique qu'à la vue Tableau.
      if (viewMode === 'table') {
        params.append('mode', mode);
        if (mode === 'actives') params.append('tout', '1'); // récupérer aussi les planifiés futurs
      }
      if (filters.concession_id)  params.append('concession_id', filters.concession_id);
      if (filters.responsable_id) params.append('responsable_id', filters.responsable_id);
      if (filters.search)         params.append('search', filters.search);
      const r = await api.get(`/tour-manager?${params}`);
      setTaches(r.data.taches || []);
    } catch (err) {
      console.error('[TourManager] Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, [mode, filters, viewMode]);

  useEffect(() => {
    loadStats();
    loadTaches();
  }, [loadStats, loadTaches]);

  useEffect(() => {
    api.get('/referentiels/responsables').then(r => setResponsables(r.data.responsables || [])).catch(() => {});
  }, []);

  const handleUpdated = () => { loadStats(); loadTaches(); };

  const handleUpdateStatutInline = async (id, newStatut) => {
    // Mise à jour optimiste immédiate : sans ça, la carte disparaît de l'overlay
    // de drag (synchrone) puis ne réapparaît dans sa nouvelle colonne qu'après
    // l'aller-retour réseau — d'où le "flash" de retour à la position d'origine.
    const previousStatut = taches.find(t => t.id === id)?.statut;
    setTaches(prev => prev.map(t => t.id === id ? { ...t, statut: newStatut } : t));
    try {
      await tourManagerApi.updateStatut(id, newStatut);
      loadStats();
    } catch (err) {
      console.error(err);
      setTaches(prev => prev.map(t => t.id === id ? { ...t, statut: previousStatut } : t));
    }
  };

  const handleDeleteTache = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer définitivement cette tâche et toutes ses photos / commentaires ?')) return;
    try {
      await tourManagerApi.deleteTache(id);
      setTaches(prev => prev.filter(t => t.id !== id));
      loadStats();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la suppression.');
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  // Drag & drop (kanban) — dépose une carte dans une autre colonne = changement de statut.
  const handleDndDragStart = useCallback(({ active }) => setActiveDragId(active.id), []);
  const handleDndDragEnd = useCallback(({ active, over }) => {
    setActiveDragId(null);
    if (!over) return;
    const targetStatut = over.id;
    const card = taches.find(t => t.id === active.id);
    if (!card || card.statut === targetStatut) return;
    handleUpdateStatutInline(active.id, targetStatut);
  }, [taches]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayStr = new Date().toISOString().split('T')[0];
  const isPlanifie = (t) => t.date_planification && t.date_planification > todayStr;
  const planifiesCount = mode === 'actives' ? taches.filter(isPlanifie).length : 0;
  const displayedTaches = !showTout && mode === 'actives'
    ? taches.filter(t => !isPlanifie(t))
    : taches;

  const sortedTaches = useMemo(() => {
    if (!sortKey) return displayedTaches;
    const prioOrder = { rouge: 3, orange: 2, jaune: 1, '': 0 };
    const arr = [...displayedTaches];
    arr.sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === 'action' || sortKey === 'responsable_nom') {
        va = (va || '').toLowerCase();
        vb = (vb || '').toLowerCase();
      } else if (sortKey === 'priorite') {
        va = prioOrder[va || ''];
        vb = prioOrder[vb || ''];
      }
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [displayedTaches, sortKey, sortDir]);

  return (
    <div className="page-container">

      {/* Onglets — toujours en premier */}
      <div className="page-tabs">
        {[
          { key: 'actives', label: '📋 Tâches',    count: (stats?.total_en_attente || 0) + (stats?.total_en_cours || 0) },
          { key: 'termine', label: '✅ Terminées', count: stats?.total_termine },
        ].map(tab => (
          <button
            key={tab.key}
            className={viewMode === 'table' && mode === tab.key ? 'active' : ''}
            onClick={() => { setViewMode('table'); setMode(tab.key); }}
          >
            {tab.label}
            {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
          </button>
        ))}
        {viewMode === 'table' && mode === 'actives' && planifiesCount > 0 && (
          <button
            className={showTout ? 'active' : ''}
            onClick={() => setShowTout(p => !p)}
            title="Tâches planifiées dans le futur"
          >
            📅 {planifiesCount} planifié{planifiesCount > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="page-toolbar">
        <input
          type="search"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
        />
        <select
          className={`page-select${filters.responsable_id ? ' active-filter' : ''}`}
          value={filters.responsable_id}
          onChange={e => setFilters(f => ({ ...f, responsable_id: e.target.value }))}
        >
          <option value="">Tous les responsables</option>
          <option value="non_assignee">Non assignée</option>
          {responsables.map(r => (
            <option key={r.id} value={r.id}>{r.prenom ? `${r.prenom} ` : ''}{r.nom}</option>
          ))}
        </select>
        {(user?.role === 'super-admin' || user?.role === 'teleoperateur') && !user?.concession_id && concessions?.length > 1 && (
          <select
            className={`page-select${filters.concession_id ? ' active-filter' : ''}`}
            value={filters.concession_id}
            onChange={e => setFilters(f => ({ ...f, concession_id: e.target.value }))}
          >
            <option value="">Toutes les concessions</option>
            {concessions.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        )}
        {mode === 'actives' && (
          <button
            className={`page-select${viewMode === 'kanban' ? ' active-filter' : ''}`}
            onClick={() => setViewMode(v => (v === 'kanban' ? 'table' : 'kanban'))}
            title="Basculer la vue Kanban — toutes les tâches par statut"
          >
            🗂 Kanban
          </button>
        )}
      </div>

      {/* Table */}
      {viewMode === 'table' && (
        loading ? (
          <div className="tm-loading">Chargement…</div>
        ) : displayedTaches.length === 0 ? (
          <div className="tm-empty">
            <div className="tm-empty-icon">{mode === 'actives' ? '✅' : '📋'}</div>
            <p>{mode === 'actives' ? 'Aucune tâche en cours' : 'Aucune tâche terminée'}</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="tm-table">
              <thead>
                <tr>
                  <SortableTh label="Date" field="created_at" sort={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Priorité" field="priorite" sort={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Action" field="action" sort={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Responsable" field="responsable_nom" sort={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Date limite" field="date_limite" sort={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Statut" field="statut" sort={sortKey} dir={sortDir} onSort={handleSort} />
                  {user?.role === 'super-admin' && <th></th>}
                </tr>
              </thead>
              <tbody>
                {sortedTaches.map(t => (
                  <tr key={t.id} className={`tm-row${t.priorite ? ` tm-row-${t.priorite}` : ''}`} onClick={() => setSelectedId(t.id)}>
                    <td className="tm-date-cell">
                      {new Date(t.created_at).toLocaleDateString('fr-FR')}
                      <div className="tm-time-cell">
                        {new Date(t.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {isPlanifie(t) && (
                        <div style={{ marginTop: '3px' }}>
                          <span style={{
                            fontSize: '10px',
                            background: '#e0f2fe',
                            color: '#0369a1',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            fontWeight: '600'
                          }}>
                            📅 {new Date(t.date_planification).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="tm-priorite-cell">
                      {(() => {
                        const p = PRIORITES.find(p => p.value === t.priorite);
                        return p
                          ? <span
                              className="tm-priorite-btn-color"
                              title={p.value.charAt(0).toUpperCase() + p.value.slice(1)}
                              style={{ background: p.color, display: 'inline-block', borderColor: p.color }}
                            />
                          : <span className="tm-priorite-none">—</span>;
                      })()}
                    </td>
                    <td className="tm-action-cell">
                      <span className="tm-action-text">
                        {t.action.length > 80 ? t.action.substring(0, 80) + '…' : t.action}
                      </span>
                      {t.concession_nom && <span className="tm-conc-tag">{t.concession_nom}</span>}
                      {Array.isArray(t.photos_data) && t.photos_data.length > 0 && (
                        <div className="tm-row-thumbs">
                          {t.photos_data.map((data, i) => (
                            <img
                              key={i}
                              src={`data:image/jpeg;base64,${data}`}
                              alt={`Photo ${i + 1}`}
                              className="tm-row-thumb"
                            />
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {t.responsable_nom
                        ? <span className="tm-resp-name">{t.responsable_nom}</span>
                        : <span className="tm-unassigned-tag">Non assignée</span>
                      }
                    </td>
                    <td>
                      {t.date_limite ? (() => {
                        const overdue = t.statut !== 'termine' && t.date_limite < todayStr;
                        return (
                          <span className={`tm-echeance-badge${overdue ? ' tm-echeance-overdue' : ''}`}>
                            {new Date(t.date_limite).toLocaleDateString('fr-FR')}
                          </span>
                        );
                      })() : <span className="tm-priorite-none">—</span>}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <InlineStatusSelect
                        value={t.statut}
                        options={STATUTS_TACHE}
                        onChange={(newStatut) => handleUpdateStatutInline(t.id, newStatut)}
                      />
                    </td>
                    {user?.role === 'super-admin' && (
                      <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center', width: '40px' }}>
                        <button
                          onClick={(e) => handleDeleteTache(e, t.id)}
                          title="Supprimer cette tâche"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            color: '#ef4444',
                            opacity: 0.6,
                            transition: 'opacity 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = 1}
                          onMouseLeave={e => e.currentTarget.style.opacity = 0.6}
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Kanban */}
      {viewMode === 'kanban' && (
        loading ? (
          <div className="tm-loading">Chargement…</div>
        ) : (
          <DndContext sensors={dndSensors} onDragStart={handleDndDragStart} onDragEnd={handleDndDragEnd}>
            <div className="tm-kanban">
              {STATUTS_TACHE.map(s => (
                <TacheKanbanColumn
                  key={s.value}
                  s={s}
                  list={taches.filter(t => t.statut === s.value)}
                  todayStr={todayStr}
                  isDragActive={!!activeDragId}
                  onOpen={setSelectedId}
                />
              ))}
            </div>
            <DragOverlay>
              {activeDragId ? (() => {
                const t = taches.find(x => x.id === activeDragId);
                if (!t) return null;
                const p = PRIORITES.find(pr => pr.value === t.priorite);
                return (
                  <div className="tm-kanban-card is-drag-overlay" style={{ borderLeftColor: p ? p.color : 'var(--border-default)' }}>
                    <KanbanCardContent t={t} todayStr={todayStr} />
                  </div>
                );
              })() : null}
            </DragOverlay>
          </DndContext>
        )
      )}

      {selectedId && (
        <TacheModal
          id={selectedId}
          user={user}
          responsables={responsables}
          onClose={() => setSelectedId(null)}
          onUpdated={handleUpdated}
        />
      )}

      {showNewTache && (
        <NouvellesTacheModal
          user={user}
          concessions={concessions}
          responsables={responsables}
          onClose={() => setShowNewTache(false)}
          onCreated={handleUpdated}
        />
      )}
    </div>
  );
}

export default TourManager;
